import { useBoolean } from '@chakra-ui/react';
import { MouseEvent, useCallback, useEffect, useState } from 'react';
import { WidgetState } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import {
  ReadOnlyWidgetBase,
  WidgetBase,
  WidgetSideMenuSettings,
  WidgetToolbar,
  useEditor,
  useWidget,
} from 'widgets/sdk';

import { SHORT_QUESTION_INPUT_TEST_ID } from './FormWidget.config';
import { FormWidgetData } from './FormWidget.types';
import FormWidgetSideMenuSettings from './FormWidgetSideMenuSettings/FormWidgetSideMenuSettings';
import { FormWidgetToolbar } from './FormWidgetToolbar';
import { FORM_TYPE_MAP } from './types';

export const FormWidget = () => {
  const [widgetState, setNewWidgetState] = useState(WidgetState.Default);
  const { field, style, labelPosition, isWidgetSelected, type } = useWidget<FormWidgetData>();
  const { boundingBox, openWidgetSideMenu } = useEditor();
  const [labelFocus, setLabelFocus] = useBoolean(false);
  const isActive = widgetState === WidgetState.Active;

  const FormBase = FORM_TYPE_MAP[type];

  const setWidgetState = useCallback(
    (newWidgetState: WidgetState) => {
      setNewWidgetState(newWidgetState);
      if (newWidgetState === WidgetState.Active || newWidgetState === WidgetState.Default) {
        setLabelFocus.off();
      } else if (newWidgetState === WidgetState.Edit) {
        setLabelFocus.on();
      }
    },
    [setNewWidgetState, setLabelFocus],
  );

  const onClick = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      if (widgetState === WidgetState.Active && e.target instanceof HTMLLabelElement) {
        setWidgetState(WidgetState.Edit);
      }
      // If click target does not match label element while being still selected, keep it in active state
      if (
        isWidgetSelected &&
        widgetState === WidgetState.Edit &&
        (e.target as HTMLElement)?.dataset.testid === SHORT_QUESTION_INPUT_TEST_ID
      ) {
        setWidgetState(WidgetState.Active);
      }
      if (widgetState === WidgetState.Default) setWidgetState(WidgetState.Active);
    },
    [isWidgetSelected, setWidgetState, widgetState],
  );

  // If the widget is not selected anymore, reset the widget state to default
  useEffect(() => {
    if (!isWidgetSelected && widgetState !== WidgetState.Default) {
      setWidgetState(WidgetState.Default);
    }
    // a widget can be selected without going through onClick (e.g. keyboard tab, drag area)
    // a selected text widget should never be in default state
    if (isWidgetSelected && widgetState === WidgetState.Default) {
      setWidgetState(WidgetState.Active);
    }
  }, [isWidgetSelected, widgetState, setWidgetState]);

  // Update bounding box when field, style or label position changes
  useEffect(() => {
    boundingBox.updateRect();
  }, [boundingBox, field, style, labelPosition]);

  return (
    <>
      <WidgetToolbar>
        <FormWidgetToolbar isActive={isActive} />
      </WidgetToolbar>
      <WidgetBase onClick={onClick} onDoubleClick={openWidgetSideMenu}>
        <WidgetSideMenuSettings>
          <FormWidgetSideMenuSettings />
        </WidgetSideMenuSettings>
        <FormBase labelFocus={labelFocus} setWidgetState={setWidgetState} />
      </WidgetBase>
    </>
  );
};

export const ReadOnlyFormWidget = () => {
  const { type } = useWidget<FormWidgetData>();
  const FormBase = FORM_TYPE_MAP[type];
  return (
    <ReadOnlyWidgetBase>
      <FormBase isReadOnly />
    </ReadOnlyWidgetBase>
  );
};
